import { collection, doc, getDoc, getDocs } from 'firebase/firestore';
import { db } from '../firebase/config';
import { AIUsageRecord, FeatureUsage, MonthlyUsageSummary } from '../types/usage';
import { DEFAULT_MONTHLY_TOKEN_ALLOWANCE, getAiAllowance, usageMonthKey } from './aiClient';

const count = (value: unknown) =>
  typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : 0;

const featureUsage = (value: any): FeatureUsage => ({
  tokens: count(value?.totalTokens),
  requests: count(value?.requestCount),
});

const emptySummary = (month: string): MonthlyUsageSummary => ({
  month,
  totalTokens: 0,
  totalInputTokens: 0,
  totalOutputTokens: 0,
  totalRequests: 0,
  chatUsage: { tokens: 0, requests: 0 },
  insightsUsage: { tokens: 0, requests: 0 },
  sessionBuilderUsage: { tokens: 0, requests: 0 },
  limit: DEFAULT_MONTHLY_TOKEN_ALLOWANCE,
});

export class AiUsageService {
  private static instance: AiUsageService;

  private constructor() {}

  static getInstance(): AiUsageService {
    if (!AiUsageService.instance) {
      AiUsageService.instance = new AiUsageService();
    }
    return AiUsageService.instance;
  }

  /**
   * Athlete-app usage for a month (UTC), across the PWA, iOS and Android.
   * aiProxy records it at aiUsage/{uid}/athlete/{month}; months before that
   * fall back to the per-request records the PWA used to write.
   */
  async getMonthlyUsageSummary(userId: string, monthKey: string = usageMonthKey()): Promise<MonthlyUsageSummary> {
    const snap = await getDoc(doc(db, `aiUsage/${userId}/athlete/${monthKey}`));
    if (snap.exists()) {
      const data = snap.data();
      return {
        month: monthKey,
        totalTokens: count(data.totalTokens),
        totalInputTokens: count(data.inputTokens),
        totalOutputTokens: count(data.outputTokens),
        totalRequests: count(data.requestCount),
        chatUsage: featureUsage(data.byFeature?.chat),
        insightsUsage: featureUsage(data.byFeature?.insights),
        sessionBuilderUsage: featureUsage(data.byFeature?.sessionBuilder),
        limit:
          typeof data.limit === 'number' && Number.isFinite(data.limit) && data.limit >= 0
            ? data.limit
            : DEFAULT_MONTHLY_TOKEN_ALLOWANCE,
      };
    }
    if (monthKey === usageMonthKey()) return emptySummary(monthKey);
    return this.getLegacyMonthlyUsageSummary(userId, monthKey);
  }

  /** This month's usage with the live limit set in PeakAdmin. */
  async getCurrentUsageSummary(userId: string): Promise<MonthlyUsageSummary> {
    const [summary, limit] = await Promise.all([
      this.getMonthlyUsageSummary(userId),
      getAiAllowance()
        .then((allowance) => allowance.limit)
        .catch((error) => {
          console.error('Error fetching AI allowance:', error);
          return null;
        }),
    ]);
    return limit === null ? summary : { ...summary, limit };
  }

  private async getLegacyMonthlyUsageSummary(userId: string, monthKey: string): Promise<MonthlyUsageSummary> {
    const summary = emptySummary(monthKey);
    const snapshot = await getDocs(collection(db, `aiUsage/${userId}/usage/${monthKey}/usage`));
    snapshot.forEach((record) => {
      const data = record.data() as AIUsageRecord;
      const tokens = count(data.tokens?.totalTokens);
      const requests = count(data.requestCount);
      summary.totalTokens += tokens;
      summary.totalInputTokens += count(data.tokens?.inputTokens);
      summary.totalOutputTokens += count(data.tokens?.outputTokens);
      summary.totalRequests += requests;
      const feature = data.source === 'chat' ? summary.chatUsage : data.source === 'insights' ? summary.insightsUsage : null;
      if (feature) {
        feature.tokens += tokens;
        feature.requests += requests;
      }
    });
    return summary;
  }
}

export const aiUsageService = AiUsageService.getInstance();
